import { useState, useEffect } from 'react';
import { ChevronDown, Maximize, Minimize } from 'lucide-react';
import { getRequest } from '../../services/apiService';
import UpdateTaskSubtaskForm from './UpdateTaskSubtaskForm';
import UpdateMileStoneTask from './UpdateMileStoneTask';

const TileLayout = ({ selectedProject, selectedShip, onViewDetails }) => {
  const tiles = [
    {
      id: 1,
      title: 'Update Task/Subtask',
      icon: '📝',
      description: 'Manage task and subtask progress',
      color: 'bg-gradient-to-br from-blue-400 to-blue-600',
      iconBg: 'bg-blue-100',
      type: 'task'
    },
    {
      id: 2,
      title: 'Update Milestone',
      icon: '🎯',
      description: 'Track milestone achievements',
      color: 'bg-gradient-to-br from-purple-400 to-purple-600',
      iconBg: 'bg-purple-100',
      type: 'milestone'
    }
  ];

  return (
    <div className="container mx-auto p-4">
      {(!selectedProject || !selectedShip) && (
        <div className="mb-4 p-4 bg-yellow-50 border-l-4 border-yellow-400">
          <p className="text-yellow-700">Please select project and ship to continue</p>
        </div>
      )}
      
      <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
        {tiles.map((tile) => (
          <div 
            key={tile.id} 
            className={`bg-white rounded-xl shadow border border-gray-100 transform transition-all duration-300 ${
              (!selectedProject || !selectedShip) ? 'opacity-50 pointer-events-none' : 'hover:-translate-y-1 hover:shadow-xl'
            }`}
          >
            <div className={`h-2 ${tile.color} rounded-t-xl`}></div>
            <div className="p-4 flex flex-col items-center justify-center">
              <div className={`text-4xl mb-3 p-4 rounded-full ${tile.iconBg} shadow-md`}>
                {tile.icon}
              </div>
              <h2 className="text-xl font-semibold text-center text-gray-800 mb-2">
                {tile.title}
              </h2>
              <p className="text-sm text-gray-600 text-center mb-3">{tile.description}</p>
              <div className="w-full px-2">
                <button
                  onClick={() => onViewDetails(tile.type)}
                  disabled={!selectedProject || !selectedShip}
                  className={`w-full py-2 rounded-lg text-sm font-medium transition-colors ${
                    (!selectedProject || !selectedShip) 
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  View Details
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function ProjectShipSelector() {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [ships, setShips] = useState([]);
  const [selectedShip, setSelectedShip] = useState('');
  const [hasShips, setHasShips] = useState(true);
  const [shipsLoading, setShipsLoading] = useState(false);
  const [showInspectionForm, setShowInspectionForm] = useState(false);
  const [showMilestoneForm, setShowMilestoneForm] = useState(false);
  // Add new states for fullscreen mode
  const [isTaskFullscreen, setIsTaskFullscreen] = useState(false);
  const [isMilestoneFullscreen, setIsMilestoneFullscreen] = useState(false);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await getRequest('master/projects/');
        setProjects(response.data);
      } catch (error) {
        console.error('Failed to fetch projects:', error);
      }
    };
    fetchProjects();
  }, []);

  useEffect(() => {
    const fetchShips = async () => {
      if (!selectedProject) return;
      
      setShipsLoading(true);
      setHasShips(true);
      setSelectedShip('');
      
      try {
        const response = await getRequest(`master/projects/?parent_id=${selectedProject}`);
        if (response.data?.length > 0) {
          setShips(response.data);
        } else {
          setShips([]);
          setHasShips(false);
        }
      } catch (error) {
        console.error('Failed to fetch ships:', error);
        setShips([]);
        setHasShips(false);
      } finally {
        setShipsLoading(false);
      }
    };
    
    fetchShips();
  }, [selectedProject]);

  const handleViewDetails = (type) => {
    if (type === 'task') {
      setShowInspectionForm(true);
      setIsTaskFullscreen(false);
    } else if (type === 'milestone') {
      setShowMilestoneForm(true);
      setIsMilestoneFullscreen(false);
    }
  };

  // Toggle fullscreen functions
  const toggleTaskFullscreen = () => {
    setIsTaskFullscreen(!isTaskFullscreen);
  };

  const toggleMilestoneFullscreen = () => {
    setIsMilestoneFullscreen(!isMilestoneFullscreen);
  };

  return (
    <div className="w-full max-w-full mx-auto">
      <div className="space-y-4 p-4">
        <div className="flex flex-row space-x-4">
          <div className="w-full">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Project</label>
            <div className="relative">
              <select 
                className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
              >
                <option value="">Select Project</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
              {/* <ChevronDown className="pointer-events-none absolute right-3 top-3" size={16} /> */}
            </div>
          </div>

          <div className="w-full">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Ship</label>
            {shipsLoading ? (
              <div className="flex justify-center py-2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
              </div>
            ) : !hasShips && selectedProject ? (
              <div className="text-gray-600 italic py-2 border rounded-lg px-3 bg-gray-50">
                No ships available for this project
              </div>
            ) : (
              <div className="relative">
                <select 
                  className={`w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm ${
                    !selectedProject ? 'bg-gray-100 cursor-not-allowed' : ''
                  }`}
                  value={selectedShip}
                  onChange={(e) => setSelectedShip(e.target.value)}
                  disabled={!selectedProject}
                >
                  <option value="">Select Ship</option>
                  {ships.map((ship) => (
                    <option key={ship.id} value={ship.id}>
                      {ship.name}
                    </option>
                  ))}
                </select>
                {/* <ChevronDown className="pointer-events-none absolute right-3 top-3" size={16} /> */}
              </div>
            )}
          </div>
        </div>

        <TileLayout 
          selectedProject={selectedProject}
          selectedShip={selectedShip}
          onViewDetails={handleViewDetails}
        />

        {showInspectionForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div 
              className={`bg-white shadow-xl overflow-auto ${
                isTaskFullscreen 
                  ? "fixed inset-0 m-0" 
                  : "rounded-lg w-full max-w-7xl max-h-screen"
              }`}
            >
              <div className="bg-blue-700 text-white p-4 flex justify-between items-center">
                <h2 className="text-xl font-bold">Update Task</h2>
                <div className="flex items-center space-x-2">
                  <button 
                    onClick={toggleTaskFullscreen}
                    className="text-white hover:text-gray-200 focus:outline-none p-1 hover:bg-blue-800 rounded-full transition-colors"
                    title={isTaskFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                  >
                    {isTaskFullscreen ? 
                      <Minimize className="w-5 h-5" /> : 
                      <Maximize className="w-5 h-5" />
                    }
                  </button>
                  <button 
                    onClick={() => setShowInspectionForm(false)}
                    className="text-white hover:text-gray-200 focus:outline-none p-1 hover:bg-blue-800 rounded-full transition-colors"
                    title="Close"
                  >
                    ✕
                  </button>
                </div>
              </div>
              <UpdateTaskSubtaskForm 
                shipId={selectedShip}
                equipmentId="EQUIP123" 
                onClose={() => setShowInspectionForm(false)}
              />
            </div>
          </div>
        )}
        
        {showMilestoneForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div 
              className={`bg-white shadow-xl overflow-auto ${
                isMilestoneFullscreen 
                  ? "fixed inset-0 m-0" 
                  : "rounded-lg w-full max-w-3xl max-h-screen"
              }`}
            >
              <div className="bg-blue-700 text-white p-4 flex justify-between items-center">
                <h2 className="text-xl font-bold">Update Milestones</h2>
                <div className="flex items-center space-x-2">
                  <button 
                    onClick={toggleMilestoneFullscreen}
                    className="text-white hover:text-gray-200 focus:outline-none p-1 hover:bg-blue-800 rounded-full transition-colors"
                    title={isMilestoneFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                  >
                    {isMilestoneFullscreen ? 
                      <Minimize className="w-5 h-5" /> : 
                      <Maximize className="w-5 h-5" />
                    }
                  </button>
                  <button 
                    onClick={() => setShowMilestoneForm(false)}
                    className="text-white hover:text-gray-200 focus:outline-none p-1 hover:bg-blue-800 rounded-full transition-colors"
                    title="Close"
                  >
                    ✕
                  </button>
                </div>
              </div>
              <UpdateMileStoneTask
                shipId={selectedShip}
                equipmentId="EQUIP123" 
                onClose={() => setShowMilestoneForm(false)}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}